/**
 * EmptyState — the one recipe for "nothing here yet" surfaces.
 *
 * Flat card with a bordered icon chip, tightened typography, and a
 * primary CTA (with optional secondary action / hint). No blur, no
 * glow, no `card-glass` — purely token-driven so light/dark flip
 * cleanly and it never fights the shell.
 */
import { LucideIcon, ArrowRight } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NovaAskChip } from '@/components/nova/NovaAskChip';
import type { ReactNode } from 'react';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  hint?: ReactNode;
  /**
   * NOVA-NUDGE-1 — the exact sentence to say to Nova for this surface.
   * When set, a tappable glass chip renders beneath the CTA and routes
   * to `/assistant?ask=<sentence>` (pre-fill only, never auto-send).
   */
  novaAsk?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = {
  sm: 'p-6',
  md: 'p-8 md:p-10',
  lg: 'p-10 md:p-14',
} as const;

export function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  hint,
  novaAsk,
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-dashed border-border bg-card/40 text-center',
        'flex flex-col items-center animate-fade-in',
        SIZES[size],
        className,
      )}
    >
      {/* BRAND-2 — the icon chip + CTA wear the SECTION accent
          (`--section-accent`, set per-route via `data-orbit-accent`):
          empty states are where a feature introduces itself, so they
          introduce its colour too.  Title/description stay neutral —
          accent ≠ wallpaper.  On unmapped routes the tokens resolve
          to Mission Blue, so nothing here ever renders unbranded. */}
      <div className="mb-4 inline-flex items-center justify-center h-11 w-11 rounded-full border border-section/30 bg-section-fill/[0.14] text-section">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-display text-base md:text-lg font-semibold text-foreground tracking-tight">
        {title}
      </h3>
      <p className="mt-1.5 text-sm text-muted-foreground max-w-sm">
        {description}
      </p>
      {(actionLabel || secondaryLabel) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {/* `section-tint` repoints --primary/--ring at the
              section accent for this button only. */}
          {actionLabel && onAction && (
            <Button onClick={onAction} className="section-tint gap-2 rounded-full px-5">
              {actionLabel}
              <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4" />
            </Button>
          )}
          {secondaryLabel && onSecondary && (
            <Button
              variant="ghost"
              onClick={onSecondary}
              className="rounded-full px-4 text-muted-foreground hover:text-foreground"
            >
              {secondaryLabel}
            </Button>
          )}
        </div>
      )}
      {novaAsk && <NovaAskChip ask={novaAsk} className={actionLabel || secondaryLabel ? 'mt-3' : 'mt-5'} />}
      {hint && (
        <p className="mt-4 text-[11px] uppercase tracking-[0.14em] text-muted-foreground/70">
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * InlineEmpty — compact variant for narrow right-rail slots
 * ("No documents yet" in the workspace side panel, etc.). Renders
 * icon · label · CTA inline without a big framed card.
 */
export function InlineEmpty({
  icon: Icon,
  label,
  actionLabel,
  onAction,
  className,
}: {
  icon: LucideIcon;
  label: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-dashed border-border/60 px-3 py-4 text-center',
        className,
      )}
    >
      <Icon className="w-4 h-4 text-muted-foreground mx-auto mb-1.5" />
      <p className="text-xs text-muted-foreground">{label}</p>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-2 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
